/**

General wrapper around multi-language services

@category Data and API Services
*/

import { DataTools } from './DataTools';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { RemoteAgent } from '../RemoteAgent/RemoteAgent';

// Library for Events that are strongly typed
// see https://github.com/vitaly-t/sub-events
import { SubEvent } from 'sub-events';

/** Available language option for this study.  Short code may be duplicated,
for example, "en" but code must be unique.  Name is used when displaying the
language as an option.

@see https://www.localeplanet.com/icu/
*/
export interface TAvailableLanguage
{
	code: string; // full ISO 639-1 code.  For example, "en-US" for English in the US while "en-CA" is English in Canada.
	short_code: string; // the short code for the language. en for English, es for Spanish
	name: string; // The selectable name such as English; Spanish
}

export class LangServices
{
	/**
	 Should be a single instance access function so that we can share common references without
	 having to reload configuration files more than once
	 */
	static _instance: LangServices;

	localLogicEngine: LogicEngine = new LogicEngine();

	/** Track the currently selected language */
	static currentLanguage: TAvailableLanguage = {
		code: "en-US",
		short_code: "en",
		name: "English (US)"
	};

	/**
	 * Event that is fired when the study language changes.
	 */
	static evLanguageChange: SubEvent<TAvailableLanguage> = new SubEvent();

	/**
	 * Get data service instance
	 * @returns instance for data service
	 */
	static instance(): LangServices
	{
		if (LangServices._instance) return LangServices._instance;
		LangServices._instance = new LangServices();
		return LangServices._instance;
	}

	constructor() { }

	/** Given some text in English, that text will be returned in the user's current language.
	If that text contains {{ 1 }} then an argument is replaced with the first argument.  For example
	T("I have {{ 1 }} pet dogs", 4);   In the future, localizations may re-write, for example, {{ 1 }} pet dogs have I.
	numbers, dates, and currency should always be used a paramter.
	*/
	Translate(strTextEnglish: string, ...args: any)
	{
		for (let n = 0; n < args.length; n++)
		{
			this.localLogicEngine.assign(n.toString(), args[n]);
		}

		RemoteAgent.instance().reportMissingLang(strTextEnglish);

		// TODO: Lookup translation
		const value = this.localLogicEngine.processTextReplacement(strTextEnglish);
		if (LangServices.currentLanguage.short_code == "en") return value;
		if (LangServices.currentLanguage.short_code == "es") return value + " (es)";

		return "[" + LangServices.currentLanguage.short_code + "]" + value;
	}

	static T(strTextEnglish: string, ...args: any)
	{
		return LangServices.instance().Translate(strTextEnglish, ...args);
	}

	/* Return a list of available languages for this study */
	async GetAvailableLanguages(): Promise<TAvailableLanguage[]>
	{
		return new Promise((resolve) =>
		{
			const tempResult: TAvailableLanguage[] = [
				{
					code: "en-US",
					short_code: "en",
					name: "English (US)"
				},
				{
					code: "es-MX",
					short_code: "es",
					name: "Español (Puerto Rico)"
				},
				// {
				// 	code: "hi",
				// 	short_code: "hi",
				// 	name: "हिन्दी (Hindi)"
				// }
			]

			resolve(tempResult);
		})
	}

	/** Set the language to be used moving forward.
	The strName may be a full code such as from window.navigator.language or  a short code
	returns true once complete or false if the language was not found.  In the case of an error,
	English is set as the default.
	*/
	async setLanguage(strName: string): Promise<boolean>
	{
		let strSearch = DataTools.internalValidateString(strName).trim().toLowerCase();
		const list = await this.GetAvailableLanguages();

		// Search the list by complete code first
		for (const item of list)
		{
			if (item.code.toLowerCase() === strSearch)
			{
				LangServices.currentLanguage = item;
				LangServices.evLanguageChange.emit(item);
				return true;
			}
		}

		// Search the list by short code last
		strSearch = strSearch.replace(/\-.*/, "");
		for (const item of list)
		{
			if (item.code.toLowerCase() === strSearch || item.short_code.toLowerCase() == strSearch)
			{
				LangServices.currentLanguage = item;
				LangServices.evLanguageChange.emit(item);
				return true;
			}
		}

		if (LogicEngine.debugOutput)
			console.warn("Language Error:  Not found:", strName);

		return false;
	}
}
